import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { LogsDataService, LogsColumnsService } from '../../services';
import { DialogLogFormComponent } from './log-form/log-form.component';
import { DialogLogDetailsComponent } from './log-details/log-details.component';
import { Log, LogsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class LogsComponent implements OnInit, OnDestroy {

  private logAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private logEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private logDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public logToDelete: Log = {};
  public logs: Log[] = [];
  public log: Log = {};
  public selectedLogs: Log[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private logsDataService: LogsDataService,
    private logsColumnsService: LogsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('LogsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Logs - Management');
    this.primengConfig.ripple = true;
    this.getLogs();
  }

  ngOnDestroy(): void {
    console.log('LogsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getLogs(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.logsDataService.getLogs(this.parameters).subscribe((response: LogsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.logs = response.results;
        } else {
          this.logs = this.logs.concat(response.results);
        }
        if (response.inlineCount > this.logs.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getLogs();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LOGSNOTLOADED')});
    });
  }

  importLogs(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.logsDataService.addLogs(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LOGSCREATED')});
                if (proceed === j) {
                  this.getLogs();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LOGSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LOGSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.logsDataService.updateLogs(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LOGSUPDATED')});
                if (proceed === j) {
                  this.getLogs();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LOGSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LOGSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.logsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('logs.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedLogs() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Logs?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.logsDataService.deleteLogs(this.selectedLogs).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Logs Deleted', life: 3000});
            this.getLogs();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Logs not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Logs not deleted'});
        });
        this.selectedLogs = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.logs, this.columns, 'Logs');
  }

  exportExcel() {
    ToolsService.exportExcel(this.logs, this.columns, 'Logs');
  }

  exportCsv() {
    ToolsService.exportCsv(this.logs, this.columns, 'Logs');
  }

  refresh() {
    this.logsDataService.clearCache();
    this.getLogs();
  }

  logAdd(): void {
    if (!this.navigation.getParamValue('logs.addInDialog', true)) {
      this.router.navigate(['/logs/add']);
      return;
    }
    this.logAddDialog = this.dialogService.open(DialogLogFormComponent, {
      header: '', // this.translate.instant('NEWLOG'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        log: {id: null},
        users: this.logsColumnsService.users,
      },
    });

    this.logAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Log added'});
        this.getLogs();
      }
    });
  }

  logEdit(log: Log): void {
    if (!this.navigation.getParamValue('logs.editInDialog', true)) {
      this.router.navigate(['/logs/edit/' + log.id]);
      return;
    }
    this.logEditDialog = this.dialogService.open(DialogLogFormComponent, {
      header: '', // this.translate.instant('EDITLOG') + ' ' + log.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        log: log,
        users: this.logsColumnsService.users,
      },
    });

    this.logEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Log updated'});
        this.getLogs();
      }
    });
  }

  logDetails(log: Log): void {
    if (!this.navigation.getParamValue('logs.detailsInDialog', true)) {
      this.router.navigate(['/logs/details/' + log.id]);
      return;
    }
    this.logDetailsDialog = this.dialogService.open(DialogLogDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        log: log,
      }
    });
  }

  logDelete(log: Log): void {
    this.logToDelete = log;
    this.messageService.clear();
    this.messageService.add({
      key: 'logDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + log.entity + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.logsDataService.deleteLog(this.logToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Log deleted'});
      this.getLogs();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Log not deleted'});
    });
  }

  onDeleteReject(): void {
    this.logToDelete = {};
    this.messageService.clear('logDelete');
  }

}
